import React, { forwardRef } from 'react';
import { Line } from 'react-chartjs-2';

const PerformanceChart = forwardRef(({ timeSeries, portfolioName }, ref) => {
  if (!timeSeries || !timeSeries.portfolio || timeSeries.portfolio.length === 0) {
    return <div className="chart-placeholder-text">No performance data available</div>;
  }

  const NAVY = '#072040';
  const GREY = '#6c757d';

  const EXTRA_SERIES_STYLES = {
    'Cash': { color: '#f0a500', dash: [5, 5] },
  };

  const portfolioData = timeSeries.portfolio
    .map(d => ({ x: new Date(d.date), y: d.value * 100 }))
    .sort((a, b) => a.x - b.x);

  const benchmarkData = timeSeries.benchmark
    .map(d => ({ x: new Date(d.date), y: d.value * 100 }))
    .sort((a, b) => a.x - b.x);

  const datasets = [
    {
      label: portfolioName || 'Portfolio',
      data: portfolioData,
      borderColor: NAVY,
      backgroundColor: 'transparent',
      borderWidth: 2,
      pointRadius: 0,
      tension: 0.1
    },
    {
      label: timeSeries.benchmark_name || 'FE AMI Growth peers',
      data: benchmarkData,
      borderColor: GREY,
      backgroundColor: 'transparent',
      borderWidth: 1.5,
      pointRadius: 0,
      tension: 0.1
    }
  ];

  // Add extra series (Cash overlay on the DDP chart)
  if (timeSeries.extra_series) {
    timeSeries.extra_series.forEach(series => {
      const style = EXTRA_SERIES_STYLES[series.name] || { color: '#999', dash: [5, 5] };
      datasets.push({
        label: series.name,
        data: series.data
          .map(d => ({ x: new Date(d.date), y: d.value * 100 }))
          .sort((a, b) => a.x - b.x),
        borderColor: style.color,
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        borderDash: style.dash,
        pointRadius: 0,
        tension: 0.1
      });
    });
  }

  const chartData = { datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 0
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        position: 'bottom',
        align: 'center',
        labels: {
          font: { size: 11 },
          usePointStyle: false,
          boxWidth: 20,
          boxHeight: 2,
          padding: 5
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'month',
          displayFormats: {
            month: 'MMM-yy'
          }
        },
        border: {
          display: false
        },
        grid: {
          display: false
        },
        ticks: {
          font: { size: 10 },
          maxTicksLimit: 8,
          maxRotation: 0,
          minRotation: 0,
          includeBounds: true,
          autoSkip: true,
          autoSkipPadding: 20
        }
      },
      y: {
        border: {
          display: false
        },
        grid: {
          color: 'rgba(0,0,0,0.1)'
        },
        ticks: {
          font: { size: 10 },
          // Round the tick label — Chart.js hands back the raw tick float
          // (e.g. -0.05000000000000004), so trim to 2dp and drop trailing zeros
          // so a ±0.3% since-inception axis and a 40% axis both read cleanly.
          callback: (value) => `${Number(Number(value).toFixed(2))}%`
        }
      }
    }
  };

  return (
    <div style={{ height: '50mm' }}>
      <Line ref={ref} data={chartData} options={options} />
    </div>
  );
});

export default PerformanceChart;
